import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { realLinkProps } from '../../components/links/realLinkProps';
import { useRouterNavigate } from '../../components/links/useEntityLinkProps';
import {
  Text,
  Button,
  Badge,
  Spinner,
  MessageBar,
  MessageBarBody,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableCellLayout,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { HistoryRegular, DocumentBulletListRegular, AddRegular, ChevronDownRegular, ChevronRightRegular } from '@fluentui/react-icons';
import { useClientFilingActivity, useClientStateTaxObligations } from '../../services/stateTaxClientsApi';
import { formatWholeDay } from '../../utils/complianceFormatters';
import { groupFilingActivity } from '../../utils/groupFilingActivity';
import type { FilingActivityItem, FilingDetail } from '../../types';
import { FilingDetailModal } from './FilingDetailModal';
import { InboundResyncPanel } from './InboundResyncPanel';
import RecordManualFilingDialog from '../../components/compliance/RecordManualFilingDialog';
import { cadenceName } from '../../utils/cadenceName';

/**
 * Recent filings feed for a client, sourced from the dept=Sales Tax Dataverse tasks (which carry
 * every obligation type, distinguished by the populated file slot — the "department" label is a
 * misnomer) plus any app-owned manual filings ("filed (manual)"). Each row is one filed return,
 * classified to its real obligation type, most-recent first. Clicking a row opens the filing-detail
 * modal (with PDF view/download). "Record filing" logs a filing no task captured. "View all tasks"
 * navigates to the per-client tasks page.
 */
const ACTIVITY_LIMIT = 10;

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  heading: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  headerActions: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
  },
  emptyHint: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    fontSize: tokens.fontSizeBase200,
  },
  row: { cursor: 'pointer' },
  section: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalS,
    overflow: 'hidden',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    width: '100%',
    padding: tokens.spacingVerticalS,
    border: 'none',
    backgroundColor: tokens.colorNeutralBackground2,
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: tokens.fontSizeBase300,
    ':hover': { backgroundColor: tokens.colorNeutralBackground2Hover },
  },
  sectionName: { fontWeight: tokens.fontWeightSemibold, flexGrow: 1 },
  sectionBody: { padding: tokens.spacingVerticalXS },
});

function toDetail(item: FilingActivityItem): FilingDetail {
  if (item.source === 'manual') {
    return {
      source: 'manual',
      obligationType: item.obligationType,
      periodLabel: item.periodLabel,
      filedOn: item.filedOn,
      filename: item.filename,
      manualFilingId: item.manualFilingId ?? '',
    };
  }
  if (item.source === 'task') {
    return {
      source: 'task',
      obligationType: item.obligationType,
      periodLabel: item.periodLabel,
      periodStart: item.periodStart,
      filedOn: item.filedOn,
      filename: item.filename,
      taskId: item.taskId ?? '',
      obligationId: item.obligationId ?? '',
      status: item.status ?? '',
      versionNumber: item.versionNumber ?? 1,
      confirmationNumber: item.confirmationNumber,
      amountDue: item.amountDue,
      bankDebitAmount: item.bankDebitAmount,
      bankDebitDate: item.bankDebitDate,
      bankDebitAccountMasked: item.bankDebitAccountMasked,
      filedBy: item.filedBy,
      priorVersions: item.priorVersions ?? [],
    };
  }
  return {
    source: 'dataverse',
    obligationType: item.obligationType,
    periodLabel: item.periodLabel,
    filedOn: item.filedOn,
    filename: item.filename,
    activityId: item.activityId,
    slotField: item.slotField,
  };
}

export function FilingActivityCard({
  stateTaxClientId,
  clientName,
}: {
  stateTaxClientId: string;
  /** Client's display name (Dataverse account name) — threaded into RecordManualFilingDialog
   * for the Flow C name-mismatch warning. */
  clientName?: string;
}) {
  const styles = useStyles();
  const { t } = useTranslation();
  const linkTo = useRouterNavigate();
  const dash = t('common.dash');

  const { data, isLoading, isError } = useClientFilingActivity(stateTaxClientId);
  const { data: obligations } = useClientStateTaxObligations(stateTaxClientId);

  const [selected, setSelected] = useState<FilingDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  // One obligation section open at a time; null = all collapsed.
  const [openObligation, setOpenObligation] = useState<string | null>(null);

  const all = data ?? [];
  const groups = groupFilingActivity(all);

  const openDetail = (item: FilingActivityItem) => {
    setSelected(toDetail(item));
    setDetailOpen(true);
  };

  return (
    <div className="card">
      <div className={styles.header}>
        <Text className={styles.heading}>
          <HistoryRegular style={{ marginRight: 6, verticalAlign: '-3px' }} />
          {t('clientDetailPage.filingActivity.title')}
        </Text>
        <div className={styles.headerActions}>
          <Button
            appearance="subtle"
            size="small"
            icon={<AddRegular />}
            onClick={() => setRecordOpen(true)}
          >
            {t('clientDetailPage.filingActivity.recordManual')}
          </Button>
          <Button
            as="a"
            appearance="subtle"
            size="small"
            icon={<DocumentBulletListRegular />}
            {...realLinkProps(`/compliance/tasks?clientId=${encodeURIComponent(stateTaxClientId)}`, { navigate: linkTo })}
          >
            {t('clientDetailPage.filingActivity.viewAll')}
          </Button>
        </div>
      </div>

      <Text className={styles.subtitle} block>
        {t('clientDetailPage.filingActivity.subtitle')}
      </Text>

      {/* Sits directly above the feed on purpose: this is the list a staff member is staring at
          when they notice a period that should be filed and isn't. The scheduled CRM sync cannot
          see completions older than its watermark, so "it'll catch up" is false here — the fix has
          to be reachable from the exact place the problem is spotted. */}
      <InboundResyncPanel stateTaxClientId={stateTaxClientId} />

      {isLoading && (
        <div style={{ padding: '12px 0' }}>
          <Spinner size="tiny" label={t('clientDetailPage.filingActivity.loading')} />
        </div>
      )}

      {isError && (
        <MessageBar intent="error" style={{ marginTop: 12 }}>
          <MessageBarBody>{t('clientDetailPage.filingActivity.loadError')}</MessageBarBody>
        </MessageBar>
      )}

      {!isLoading && !isError && all.length === 0 && (
        <Text className={styles.emptyHint} style={{ display: 'block', padding: '12px 0' }}>
          {t('clientDetailPage.filingActivity.empty')}
        </Text>
      )}

      {groups.map((group) => {
        const open = openObligation === group.obligationType;
        const visible = group.items.slice(0, ACTIVITY_LIMIT);
        const overflow = Math.max(0, group.items.length - visible.length);
        return (
          <div key={group.obligationType} className={styles.section}>
            <button
              type="button"
              className={styles.sectionHeader}
              aria-expanded={open}
              onClick={() => setOpenObligation(open ? null : group.obligationType)}
            >
              {open ? <ChevronDownRegular /> : <ChevronRightRegular />}
              <span className={styles.sectionName}>{group.obligationType}</span>
              <Badge appearance="tint" color="informative">{group.items.length}</Badge>
            </button>
            <div className={styles.sectionBody} style={{ display: open ? 'block' : 'none' }}>
              <Table size="small" aria-label={group.obligationType}>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>{t('clientDetailPage.filingActivity.columns.period')}</TableHeaderCell>
                    <TableHeaderCell>{t('clientDetailPage.filingActivity.columns.filed')}</TableHeaderCell>
                    <TableHeaderCell>{t('clientDetailPage.filingActivity.columns.document')}</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visible.map((item, i) => (
                    <TableRow
                      key={`${item.obligationType}-${item.periodStart}-${i}`}
                      className={styles.row}
                      tabIndex={0}
                      aria-label={t('clientDetailPage.filingActivity.rowAction', { period: item.periodLabel })}
                      onClick={() => openDetail(item)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openDetail(item);
                        }
                      }}
                    >
                      <TableCell>
                        <TableCellLayout description={cadenceName(t, item.cadence)}>
                          {item.periodLabel}
                          {item.source === 'manual' && (
                            <Badge appearance="tint" color="warning" size="small" style={{ marginLeft: 6 }}>
                              {t('clientDetailPage.filingActivity.manualBadge')}
                            </Badge>
                          )}
                        </TableCellLayout>
                      </TableCell>
                      <TableCell>
                        <TableCellLayout>{item.filedOn ? formatWholeDay(item.filedOn) : dash}</TableCellLayout>
                      </TableCell>
                      <TableCell>
                        <TableCellLayout truncate>{item.filename ?? dash}</TableCellLayout>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {overflow > 0 && (
                <Text className={styles.subtitle} style={{ display: 'block', marginTop: 8, fontStyle: 'italic' }}>
                  {t('common.moreCount', { count: overflow })}
                </Text>
              )}
            </div>
          </div>
        );
      })}

      {detailOpen && (
        <FilingDetailModal
          open={detailOpen}
          onOpenChange={setDetailOpen}
          stateTaxClientId={stateTaxClientId}
          detail={selected}
        />
      )}
      {recordOpen && (
        <RecordManualFilingDialog
          open={recordOpen}
          onOpenChange={setRecordOpen}
          stateTaxClientId={stateTaxClientId}
          obligations={obligations ?? []}
          clientName={clientName}
        />
      )}
    </div>
  );
}
